import "./TradeSimulationDataComponent.scss";
import {IComponentOptions, IOnDestroy, IOnInit, IScope} from "angular";
import {Simulation} from "../../models/Simulation";
import {BehaviorSubject, Observable, ReplaySubject, Subject} from "rxjs";
import {SimulationTradeDetail} from "./models/Trade";
import {Expiries} from "../../../services/expiry/Expiries";
import {noop} from "app/js/util/UtilFunctions";
import {TradeSimulatorViewStore} from "../../service/TradeSimulatorViewStore";
import {SimulationTrade} from "../../models/SimulationTrade";
import {SimulationCalculationResult} from "../../models/SimulationCalculationResult";
import {OptionChainService} from "../../../services/option-chain/OptionChainService";
import {IsoDate} from "../../../pseudo-types/DateTypes";

export const SimulationData = "tfTradeSimulationData";

export function simulationData(): IComponentOptions {
    return {
        templateUrl: "app/trade-simulator/components/simulation-data/TradeSimulationDataComponent.html",
        bindings: {
            viewStore: "<"
        },
        controllerAs: "ctrl",
        controller: TradeSimulationDataComponentController
    };
}

export class TradeSimulationDataComponentController implements IOnInit, IOnDestroy {

    public viewStore: TradeSimulatorViewStore;

    public validExpiries: Array<Date>;
    public hideExpiredPositions: boolean = false;

    public simulation: Simulation | null;
    public selectedDate: IsoDate | null;
    public today = new Date();

    public visibleTrades$: Observable<Array<SimulationTradeDetail>>;
    private hideExpired$ = new BehaviorSubject(false);
    private trades$: Observable<Array<SimulationTradeDetail>>;

    private changes$ = new Subject();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private $scope: IScope, private optionChainService: OptionChainService) {
        this.changes$
            .takeUntil(this.destroyed$)
            .debounceTime(250)
            .subscribe(this.checkForChanges.bind(this), noop);
    }

    public $onInit(): void {
        const sources = [this.viewStore.currentSimulation, this.viewStore.currentDate, this.viewStore.expiries];
        Observable.combineLatest(...sources)
                  .takeUntil(this.destroyed$)
                  .subscribe(([simulation, date, expiries]) => {
                      this.selectedDate = date;
                      this.simulation = simulation;
                      this.initValidExpiries(expiries, simulation);
                      this.$scope.$applyAsync();
                  });

        this.trades$ = this.viewStore
                           .trades
                           .takeUntil(this.destroyed$)
                           .switchMap((trades) => this.watchDataChanges(trades))
                           .shareReplay(1);

        this.visibleTrades$ = Observable.combineLatest(this.trades$, this.hideExpired$)
                                        .takeUntil(this.destroyed$)
                                        .withLatestFrom(this.viewStore.currentDate)
                                        .map(([[trades, hideExpired], currentDate]) => {
                                            if (!hideExpired || currentDate == null) {
                                                return trades;
                                            } else {
                                                return trades.filter((tr) => !tr.hasExpired(currentDate));
                                            }
                                        })
                                        .do(() => this.$scope.$applyAsync());
    }

    private watchDataChanges(trades: Array<SimulationTrade>): Observable<Array<SimulationTradeDetail>> {
        return Observable.combineLatest(this.viewStore.currentDate, this.viewStore.results)
                         .takeUntil(this.destroyed$)
                         .map(([currentDate, results]) => {
                             if (currentDate == null) {
                                 return [];
                             } else {
                                 return this.buildTrades(trades, results, currentDate);
                             }
                         });
    }

    private buildTrades(trades: Array<SimulationTrade>,
                        results: SimulationCalculationResult | null,
                        currentDate: string): Array<SimulationTradeDetail> {
        return trades.map((tr) => {
            return SimulationTradeDetail.create(currentDate, tr, results);
        }).sort((t1, t2) => t1.trade.date.localeCompare(t2.trade.date));
    }

    public toggleHideExpiredPositions() {
        this.hideExpiredPositions = !this.hideExpiredPositions;
        this.hideExpired$.next(this.hideExpiredPositions);
    }

    private checkForChanges() {
        if (this.simulation != null && this.simulation.hasChanges) {
            this.viewStore.update(this.simulation);
        }
    }

    private queueChangeDetection() {
        this.changes$.next();
    }

    public tradeChanged() {
        this.queueChangeDetection();
    }

    public nameChanged() {
        this.queueChangeDetection();
    }

    public balanceChanged() {
        this.queueChangeDetection();
    }

    public addTrade(code: string, quantity: number, date: IsoDate, price: number) {
        if (this.simulation != null) {
            const updated = this.simulation.addTrade(code, quantity, date, price);
            this.viewStore.update(updated);
        }
    }

    public async closeTrade(tradeDetail: SimulationTradeDetail) {
        const tradeDate = this.selectedDate;
        if (tradeDate  != null && this.simulation != null) {
            const trade = tradeDetail.trade;
            const code = trade.code;
            const quantity = -trade.quantity;

            const chain = await this.optionChainService.chainFor(this.simulation.underlying, tradeDate);
            chain.contractForCode(trade.code).ifPresent((contract) => {
                this.addTrade(code, quantity, tradeDate, contract.closePrice);
            });
        }
    }

    public removeTrade(trade: SimulationTrade) {
        if (this.simulation != null) {
            const updated = this.simulation.removeTrade(trade);
            this.viewStore.update(updated);
        }
    }

    private initValidExpiries(expiries: Expiries | null, simulation: Simulation | null) {
        if (expiries == null || simulation == null) {
            return [];
        }
        this.validExpiries = expiries.forUnderlying(simulation.underlying)
                                     .ofType("MONTHLY", "QUARTERLY")
                                     .expiryDates()
                                     .map(e => new Date(e));
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
